import React, { useState, useEffect, useMemo } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { getGPS, getOpponents, getTrackingComparisonByMatch, getTrackingMatches, patchTrackingCalibration, getTrackingPeriodsByMatch } from '../../api';
import { useUrlParam } from '../../hooks/useUrlParam';

// Design tokens — design 2a ("Physical Load Comparison"), docs/design/team-comparison/. Colours
// follow team IDENTITY, not match side: Heracles is always red, the opponent always blue,
// regardless of which one happens to be home this match.
const INK = '#1B2A41', INK2 = '#16233A';
const PAGE_BG = '#EEF2F7', CARD_BG = '#FFF', TRACK_BG = '#F2F5F9', HAIRLINE = '#EEF2F7';
const HERACLES = '#C81E3C', HERACLES_LIGHT = '#E8607C';
const OPPONENT = '#35608F', OPPONENT_LIGHT = '#6E9BC8';
const MUTED_LIGHT = '#5B6E86', SECONDARY_LIGHT = '#566A82';
const MUTED_DARK = '#AEBED2', TERTIARY_DARK = '#7E93AE';
const TIE_COLOR = '#4C607A';

const FONT_DISPLAY = "'Barlow Condensed', sans-serif";
const FONT_BODY = "'Barlow', Helvetica, sans-serif";
const FONT_MONO = "'IBM Plex Mono', monospace";

const FACTOR_SOURCE_LABEL = { match: 'This match', pooled: 'Pooled', seed: 'Seed', none: 'Not converted' };

// "Heracles Almelo" -> "HER"; otherwise the first 3 letters after stripping a small set of
// common club-name stopwords, per the design spec ("strip FC|SC|Jong|De, plus a small override
// map"). Purely a display derivation — no backend data needed, so it stays client-side.
const ABBREV_OVERRIDES = { 'Heracles Almelo': 'HER' };
function teamAbbrev(name) {
  if (!name) return '???';
  if (ABBREV_OVERRIDES[name]) return ABBREV_OVERRIDES[name];
  const stripped = name.replace(/\b(FC|SC|Jong|De)\b/gi, ' ').replace(/\s+/g, ' ').trim();
  return (stripped || name).slice(0, 3).toUpperCase();
}

// dec comes from the server per metric (totalsDecimals: team totals are whole numbers unless a
// non-tied pair would print identically), the same rule as the Period Comparison block totals.
const fmt = (v, dec = 0) => (v == null || isNaN(v) ? '—' : v.toLocaleString('en-US', { minimumFractionDigits: dec, maximumFractionDigits: dec }));

function identityColor(side, heraclesSide, light = false) {
  const isHeracles = side === heraclesSide;
  if (isHeracles) return light ? HERACLES_LIGHT : HERACLES;
  return light ? OPPONENT_LIGHT : OPPONENT;
}

function MetricRow({ m, heraclesSide, basis }) {
  const leaderColor = m.leader === 'tie' ? TIE_COLOR : identityColor(m.leader, heraclesSide);
  const leaderPill = m.leader === 'tie' ? 'TIE' : (m.leader === heraclesSide ? 'HERACLES' : (m.leader === 'home' ? m._homeName : m._awayName).toUpperCase());
  const lanes = [
    { side: 'home', val: m.home, barW: m.homeBarW, code: m._homeCode },
    { side: 'away', val: m.away, barW: m.awayBarW, code: m._awayCode },
  ];
  return (
    <div style={{ padding: '14px 0', borderBottom: `1px solid ${HAIRLINE}`, display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ fontFamily: FONT_DISPLAY, fontSize: 19, fontWeight: 600, color: INK }}>{m.label}</div>
        <div style={{ fontFamily: FONT_MONO, fontSize: 10, color: MUTED_LIGHT }}>{m.sub}</div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ fontFamily: FONT_DISPLAY, fontSize: 13, fontWeight: 700, letterSpacing: '.08em', color: '#FFF', background: leaderColor, padding: '2px 8px', borderRadius: 3 }}>{leaderPill}</div>
          <div style={{ width: 62, textAlign: 'right', fontFamily: FONT_DISPLAY, fontSize: 19, fontWeight: 700, color: leaderColor }}>{m.deltaLabel}</div>
        </div>
      </div>
      {lanes.map(lane => {
        const isLeader = m.leader === lane.side;
        const fillColor = identityColor(lane.side, heraclesSide);
        return (
          <div key={lane.side} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 40, fontFamily: FONT_MONO, fontSize: 10, color: MUTED_LIGHT }}>{lane.code}</div>
            <div style={{ flex: 1, height: 24, background: TRACK_BG, borderRadius: 3, position: 'relative' }}>
              <div style={{
                position: 'absolute', inset: '0 auto 0 0', background: fillColor, borderRadius: 3,
                width: `${Math.max(lane.barW, 0)}%`, display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                paddingRight: 9, boxSizing: 'border-box',
                outline: isLeader ? `3px solid ${INK}` : '3px solid transparent', outlineOffset: 2,
              }}>
                <span style={{ fontFamily: FONT_DISPLAY, fontSize: 17, fontWeight: 700, color: '#FFF' }}>{fmt(lane.val, m.dec)}</span>
              </div>
            </div>
          </div>
        );
      })}
      {basis === 'heralabx' && !m.converted && (
        <div style={{ fontFamily: FONT_MONO, fontSize: 10, color: MUTED_LIGHT }}>not converted — no GPS sprint data</div>
      )}
    </div>
  );
}

function ComparisonCard({ data, basis }) {
  const { matchMeta, metrics, tally } = data;
  const heraclesSide = matchMeta.heraclesSide;
  const homeCode = teamAbbrev(matchMeta.home);
  const awayCode = teamAbbrev(matchMeta.away);
  const homeName = matchMeta.home, awayName = matchMeta.away;
  const enriched = metrics.map(m => ({ ...m, _homeCode: homeCode, _awayCode: awayCode, _homeName: homeName, _awayName: awayName }));

  const metaLine = [
    String(matchMeta.date).slice(0, 10), `${matchMeta.scoreHome} – ${matchMeta.scoreAway}`,
    `${matchMeta.durationMin.toFixed(1)} min`, 'team totals', `${matchMeta.nHome} v ${matchMeta.nAway} outfield players`,
  ].join(' · ') + (basis === 'heralabx' ? ' · HeraLabX-equivalent' : '');

  return (
    <div style={{ maxWidth: 900, width: '100%', background: CARD_BG, borderRadius: 12, boxShadow: '0 1px 2px rgba(27,42,65,.08)', overflow: 'hidden' }}>
      <div style={{ background: INK, padding: '24px 32px 26px', display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div>
          <div style={{ fontFamily: FONT_DISPLAY, fontSize: 26, fontWeight: 700, color: '#FFF', lineHeight: 1.1 }}>
            Physical Load — {homeName} v {awayName}
          </div>
          <div style={{ fontFamily: FONT_MONO, fontSize: 11, color: MUTED_DARK, letterSpacing: '.06em' }}>{metaLine}</div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: 14, background: INK2, borderRadius: 8, padding: '18px 26px' }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontFamily: FONT_MONO, fontSize: 10, letterSpacing: '.14em', color: MUTED_DARK }}>{homeName.toUpperCase()}</div>
            <div style={{ fontFamily: FONT_DISPLAY, fontSize: 54, fontWeight: 700, color: identityColor('home', heraclesSide, true), lineHeight: 1 }}>{tally.home}</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontFamily: FONT_DISPLAY, fontSize: 14, letterSpacing: '.1em', color: MUTED_DARK }}>{tally.tie} TIED</div>
            <div style={{ fontFamily: FONT_MONO, fontSize: 9, letterSpacing: '.12em', color: TERTIARY_DARK }}>CATEGORIES WON</div>
          </div>
          <div>
            <div style={{ fontFamily: FONT_MONO, fontSize: 10, letterSpacing: '.14em', color: MUTED_DARK }}>{awayName.toUpperCase()}</div>
            <div style={{ fontFamily: FONT_DISPLAY, fontSize: 54, fontWeight: 700, color: identityColor('away', heraclesSide, true), lineHeight: 1 }}>{tally.away}</div>
          </div>
        </div>
      </div>
      <div style={{ padding: '10px 32px 26px', display: 'flex', flexDirection: 'column' }}>
        {enriched.map(m => <MetricRow key={m.key} m={m} heraclesSide={heraclesSide} basis={basis} />)}
        <div style={{ fontFamily: FONT_MONO, fontSize: 10, color: MUTED_LIGHT, paddingTop: 12 }}>
          Bars to true scale · outline + label = leader · team totals, outfield players only ({matchMeta.nHome} v {matchMeta.nAway})
        </div>
      </div>
    </div>
  );
}

function CardSkeleton() {
  return (
    <div style={{ maxWidth: 900, width: '100%', background: CARD_BG, borderRadius: 12, boxShadow: '0 1px 2px rgba(27,42,65,.08)', overflow: 'hidden' }}>
      <div style={{ background: INK, padding: '24px 32px 26px', height: 120 }} />
      <div style={{ padding: '10px 32px 26px' }}>
        {[0, 1, 2, 3, 4].map(i => (
          <div key={i} style={{ padding: '14px 0', borderBottom: `1px solid ${HAIRLINE}` }}>
            <div style={{ width: 160, height: 14, background: TRACK_BG, borderRadius: 4, marginBottom: 10 }} />
            <div style={{ height: 24, background: TRACK_BG, borderRadius: 3, marginBottom: 6 }} />
            <div style={{ height: 24, background: TRACK_BG, borderRadius: 3 }} />
          </div>
        ))}
      </div>
    </div>
  );
}

function QualityBanner({ matchMeta }) {
  if (!matchMeta.qualityFlag && !matchMeta.gpsIncomplete) return null;
  return (
    <div style={{ maxWidth: 900, width: '100%', background: '#FDECEC', border: '1px solid #F3B8B8', borderRadius: 8, padding: '12px 16px', fontFamily: FONT_BODY, fontSize: 13, color: '#7A1F1F' }}>
      {matchMeta.qualityFlag === 'suspect_pitch_calibration' && (
        <div>Physical data looks unreliable (low high-speed distance for both teams) — the pitch calibration was probably wrong. This match is not used for the conversion factor.</div>
      )}
      {matchMeta.gpsIncomplete && (
        <div>Not all Heracles outfield players in this file have GPS data for this match — this match's own conversion factor is unreliable; falling back to the pooled/seed factor.</div>
      )}
      {matchMeta.exclusionReason && <div style={{ marginTop: 4, opacity: 0.8 }}>{matchMeta.exclusionReason}</div>}
    </div>
  );
}

function OmrekeningTable({ data, user, onToggleExclusion }) {
  const [open, setOpen] = useState(false);
  const { calibration, matchMeta } = data;
  const decOf = Object.fromEntries((data.metrics || []).map(m => [m.key, m.dec]));
  if (!calibration?.length) return null;
  return (
    <div style={{ maxWidth: 900, width: '100%' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', padding: '6px 0', fontFamily: FONT_BODY, fontSize: 13, fontWeight: 600, color: SECONDARY_LIGHT }}
      >
        Conversion {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>
      {open && (
        <div style={{ background: CARD_BG, borderRadius: 8, padding: 16, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: FONT_BODY, fontSize: 13 }}>
            <thead>
              <tr style={{ color: MUTED_LIGHT, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em' }}>
                <th style={{ textAlign: 'left', padding: '4px 8px' }}>Metric</th>
                <th style={{ textAlign: 'right', padding: '4px 8px' }}>Heracles KNVB</th>
                <th style={{ textAlign: 'right', padding: '4px 8px' }}>Heracles HeraLabX</th>
                <th style={{ textAlign: 'right', padding: '4px 8px' }}>Factor</th>
                <th style={{ textAlign: 'left', padding: '4px 8px' }}>Source</th>
                <th style={{ textAlign: 'right', padding: '4px 8px' }}>Opponent KNVB</th>
                <th style={{ textAlign: 'right', padding: '4px 8px' }}>Opponent HeraLabX</th>
              </tr>
            </thead>
            <tbody>
              {calibration.map(c => (
                <tr key={c.key} style={{ borderTop: `1px solid ${HAIRLINE}` }}>
                  <td style={{ padding: '6px 8px' }}>{c.key}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right' }}>{fmt(c.trackingHeracles, decOf[c.key])}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right' }}>{fmt(c.gpsHeracles, decOf[c.key])}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right' }}>{c.factor != null ? c.factor.toFixed(3) : '—'}</td>
                  <td style={{ padding: '6px 8px' }}>
                    {FACTOR_SOURCE_LABEL[c.factorSource] || '—'}
                    {c.factorSource === 'pooled' && c.factorN != null ? ` (n=${c.factorN})` : ''}
                  </td>
                  <td style={{ padding: '6px 8px', textAlign: 'right' }}>{fmt(c.trackingOpponent, decOf[c.key])}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right' }}>{fmt(c.convertedOpponent, decOf[c.key])}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ marginTop: 10, fontSize: 11, color: MUTED_LIGHT }}>
            Opponent (HeraLabX) = Opponent KNVB × (Heracles HeraLabX ÷ Heracles KNVB)
          </div>
          {user?.isAdmin && (
            <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${HAIRLINE}` }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: MUTED_LIGHT, cursor: 'pointer' }}>
                <input type="checkbox" checked={!!matchMeta.excludedFromCalibration} onChange={e => onToggleExclusion(e.target.checked)} />
                Excluded from calibration
              </label>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function TeamComparison({ user, onNavigate }) {
  const [sessionIdRaw, setSessionIdRaw] = useUrlParam('session_id', '');
  const sessionId = sessionIdRaw === '' ? null : Number(sessionIdRaw);
  const setSessionId = id => setSessionIdRaw(id == null ? '' : id);
  const [basis, setBasis] = useUrlParam('basis', 'heralabx');

  const [sessions, setSessions] = useState([]);
  const [opponents, setOpponents] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [data, setData] = useState(null);
  const [loadingData, setLoadingData] = useState(false);
  const [notFound, setNotFound] = useState(false);
  // Period Comparison (2026-09-28): whether this match has stored 5-minute periods — drives the
  // "Advanced comparison" button (disabled with a hint when not).
  const [hasPeriods, setHasPeriods] = useState(false);

  useEffect(() => {
    if (!sessionId) { setHasPeriods(false); return; }
    getTrackingPeriodsByMatch(sessionId)
      .then(meta => setHasPeriods(!!meta?.hasPeriods))
      .catch(() => setHasPeriods(false));
  }, [sessionId]);

  useEffect(() => {
    Promise.all([getGPS(), getOpponents()])
      .then(([gpsSessions, opps]) => {
        const match = (gpsSessions || []).filter(s => s.session_type === 'match');
        setSessions(match);
        setOpponents(opps || []);
        if (!sessionId && match.length > 0) setSessionId(match[0].id);
      })
      .finally(() => setLoadingList(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!sessionId) return;
    setLoadingData(true);
    setNotFound(false);
    getTrackingComparisonByMatch(sessionId, basis)
      .then(result => setData(result))
      .catch(() => { setData(null); setNotFound(true); })
      .finally(() => setLoadingData(false));
  }, [sessionId, basis]);

  const handleToggleExclusion = async (excluded) => {
    if (!data) return;
    // trackingMatchId isn't in the comparison payload directly — but the exclusion toggle only
    // needs an id, and by-match lookup already proved exactly one tracking_matches row exists
    // for this session, so re-resolving it via the same by-match response is unnecessary
    // plumbing; the PATCH route id param is the tracking_matches.id, fetched once here.
    try {
      const list = await getTrackingMatches();
      const row = (list || []).find(m => m.heracles_match_id === sessionId);
      if (!row) return;
      await patchTrackingCalibration(row.id, excluded, excluded ? 'manual' : null);
      const result = await getTrackingComparisonByMatch(sessionId, basis);
      setData(result);
    } catch (err) {
      console.error('Failed to update calibration exclusion:', err);
    }
  };

  const selStyle = { padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', cursor: 'pointer', fontFamily: 'inherit' };
  const navBtn = active => ({
    padding: '7px 14px', borderRadius: 8, border: `1px solid ${active ? '#3b82f6' : 'var(--border-strong)'}`,
    background: active ? '#3b82f6' : 'transparent', color: active ? '#fff' : 'var(--text-muted)',
    cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
  });

  const filteredSessions = useMemo(() => [...sessions].sort((a, b) => new Date(b.session_date) - new Date(a.session_date)), [sessions]);

  if (loadingList) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: 'var(--text-dimmed)', fontSize: 14 }}>
      Loading…
    </div>
  );

  return (
    <div style={{ background: PAGE_BG, margin: '-24px', padding: 24, minHeight: 'calc(100vh - 48px)', display: 'flex', flexDirection: 'column', gap: 20, alignItems: 'center' }}>
      <div style={{ maxWidth: 900, width: '100%', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <select value={sessionId || ''} onChange={e => setSessionId(Number(e.target.value))} style={selStyle}>
          {filteredSessions.length === 0 && <option value="">No match sessions yet</option>}
          {filteredSessions.map(s => {
            const opp = opponents.find(o => o.id === s.opponent_id);
            return <option key={s.id} value={s.id}>{opp?.name || s.session_title} — {s.session_date?.slice(0, 10)}</option>;
          })}
        </select>
        <div style={{ display: 'flex', gap: 6 }}>
          <button style={navBtn(basis === 'heralabx')} onClick={() => setBasis('heralabx')}>HeraLabX</button>
          <button style={navBtn(basis === 'knvb')} onClick={() => setBasis('knvb')}>KNVB</button>
        </div>
        {/* session_id and basis are already in the URL, so the period page picks them up as-is. */}
        <button
          style={{ ...navBtn(false), opacity: hasPeriods ? 1 : 0.45, cursor: hasPeriods ? 'pointer' : 'not-allowed' }}
          disabled={!hasPeriods}
          title={hasPeriods ? 'Compare per block of 5-minute periods' : 'No 5-minute periods for this match — re-upload the KNVB fysiek file (overwrite) in the Upload Zone'}
          onClick={() => onNavigate && onNavigate('gps', 'period-comparison')}
        >
          Advanced comparison
        </button>
        {!hasPeriods && data && (
          <span style={{ fontSize: 11, color: MUTED_LIGHT, fontFamily: FONT_BODY }}>no periods — re-upload to enable Advanced comparison</span>
        )}
      </div>

      {loadingData && <CardSkeleton />}

      {!loadingData && notFound && (
        <div style={{ maxWidth: 900, width: '100%', background: CARD_BG, borderRadius: 12, padding: 32, textAlign: 'center', fontFamily: FONT_BODY }}>
          <div style={{ fontSize: 15, color: INK, fontWeight: 600, marginBottom: 6 }}>No KNVB tracking file uploaded for this match yet</div>
          <div
            onClick={() => onNavigate && onNavigate('gpsupload')}
            style={{ display: 'inline-block', marginTop: 8, cursor: 'pointer', color: '#3b82f6', textDecoration: 'underline', fontSize: 13 }}
          >
            Upload one in the Upload Zone
          </div>
        </div>
      )}

      {!loadingData && data && (
        <>
          <ComparisonCard data={data} basis={basis} />
          <QualityBanner matchMeta={data.matchMeta} />
          {basis === 'heralabx' && <OmrekeningTable data={data} user={user} onToggleExclusion={handleToggleExclusion} />}
        </>
      )}
    </div>
  );
}
